import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  model,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { finalize, forkJoin } from 'rxjs';

import { ScreenFieldModel } from '@models/module/fields/main';
import { SearchViewModel } from '@models/module/grid';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { Currency, CurrencyFn } from '@models/system';
import { ScreenTypeEnum } from '@module-definition/screen/common';
import { CalendarDialogDocDetailPrefix } from '@module-instance/calendar/calendar-dialog-doc-detail/calendar-dialog-doc-detail.component';
import { CalendarDialogMorePrefix } from '@module-instance/calendar/calendar-dialog-more/calendar-dialog-more.component';
import { CalendarMonthViewComponent } from '@module-instance/calendar/calendar-month-view/calendar-month-view.component';
import { CalendarYearViewComponent } from '@module-instance/calendar/calendar-year-view/calendar-year-view.component';
import { CalendarDate, CalendarReferenceField, CalendarViewMode } from '@module-instance/calendar/calendar.model';
import { CalendarService } from '@module-instance/calendar/calendar.service';
import { DataCardsViewService } from '@module-instance/data-card/data-cards-view/data-cards-view.service';
import { LocaleDateAdapter, NotificationService } from '@shared/services';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
  host: {
    '(document:click)': 'clickout($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    MatDividerModule,
    CalendarMonthViewComponent,
    CalendarYearViewComponent,
  ],
})
export class CalendarViewComponent implements OnInit, OnChanges {
  readonly userStore = inject(UserStore);
  private readonly dateAdapter = inject(LocaleDateAdapter);
  private readonly translate = inject(TranslateService);
  private readonly calendarService = inject(CalendarService);
  private readonly dataCardsViewService = inject(DataCardsViewService);
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly ref = inject(ChangeDetectorRef);

  readonly actions = input<DataListViewAction[]>();
  readonly search = input<SearchViewModel>();

  /**
   * @deprecated This input is deprecated and will be removed in future versions.
   */
  readonly contractId = input<number>(undefined, { alias: 'contractid' });

  /**
   * @deprecated This input is deprecated and will be removed in future versions.
   */
  readonly currency = input<Currency | CurrencyFn>();
  readonly reloading = input<boolean>();

  readonly isLoading = model<boolean>();

  today: Date;
  calendarDate: CalendarDate;
  calendarViewMode: CalendarViewMode = CalendarViewMode.Month;
  isViewLoading: boolean;
  showWeekends: boolean = false;
  showWeekNumber: boolean = false;
  calendarDateField: CalendarReferenceField;
  calendarDescField: CalendarReferenceField;
  screenFields: ScreenFieldModel[];
  dateFields: CalendarReferenceField[];
  loadingConfig = false;

  ngOnInit(): void {
    this.today = this.dateAdapter.today();
    this.setToday();
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reloading?.currentValue) {
      // when reloading is true, reload config
      this.loadConfig();
    }
  }

  clickout(event: any) {
    this.closeAllCalendarDialogs(event);
  }

  get monthName(): string {
    return moment(this.calendarDate.date).format('MMMM');
  }

  get monthNames(): string[] {
    return this.dateAdapter.getMonthNames('long');
  }

  get calendarViewModeType(): typeof CalendarViewMode {
    return CalendarViewMode;
  }

  get prevLabel(): string {
    switch (this.calendarViewMode) {
      case CalendarViewMode.Day:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_PREV_DAY');
      case CalendarViewMode.Week:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_PREV_WEEK');
      case CalendarViewMode.Month:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_PREV_MONTH');
      case CalendarViewMode.Year:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_PREV_YEAR');
      default:
        return '';
    }
  }

  get nextLabel(): string {
    switch (this.calendarViewMode) {
      case CalendarViewMode.Day:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_NEXT_DAY');
      case CalendarViewMode.Week:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_NEXT_WEEK');
      case CalendarViewMode.Month:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_NEXT_MONTH');
      case CalendarViewMode.Year:
        return this.translate.instant('MODULE_INSTANCE_CALENDAR.BUTTON_NEXT_YEAR');
      default:
        return '';
    }
  }

  get yearsSelection(): number[] {
    const year = this.today.getFullYear() + 5;
    return [...Array(16).keys()].map(n => year - n);
  }

  get monthsSelection(): number[] {
    return [...Array(12).keys()];
  }

  setToday(): void {
    this.setCalendarDate(moment(this.today));
  }

  setYear(value: number): void {
    this.setCalendarDate(moment(this.calendarDate.date).year(value));
  }

  setMonth(value: number): void {
    this.setCalendarDate(moment(this.calendarDate.date).month(value));
  }

  incrementDecrementDate(decrement: boolean): void {
    const m = moment(this.calendarDate.date);

    if (this.calendarViewMode == CalendarViewMode.Year) {
      m.add(decrement ? -1 : 1, 'y');
    }
    if (this.calendarViewMode == CalendarViewMode.Month) {
      m.add(decrement ? -1 : 1, 'M');
    }
    if (this.calendarViewMode == CalendarViewMode.Week) {
      m.add(decrement ? -1 : 1, 'w');
    }
    if (this.calendarViewMode == CalendarViewMode.Day) {
      m.add(decrement ? -1 : 1, 'd');
    }
    this.setCalendarDate(m);
  }

  viewIsLoading(value: boolean): void {
    this.isViewLoading = value;
  }

  setDateField(field: CalendarReferenceField) {
    this.calendarDateField = field;
  }

  matchDateField(field: CalendarReferenceField) {
    return (
      this.calendarDateField &&
      ((this.calendarDateField.id && field.id && this.calendarDateField.id == field.id) ||
        (this.calendarDateField.name && field.name && this.calendarDateField.name == field.name))
    );
  }

  gotoMonthView(date: CalendarDate): void {
    this.calendarViewMode = CalendarViewMode.Month;
    this.setCalendarDate(moment(date.date));
  }

  private loadConfig(): void {
    if (this.loadingConfig) {
      return;
    }

    this.loadingConfig = true;

    forkJoin({
      descReferenceFields: this.calendarService.getDescriptionReferenceFields(),
      dateReferenceFields: this.calendarService.getDateReferenceFields(),
      screenFields: this.dataCardsViewService.getScreenFields(ScreenTypeEnum.Calendar),
      screen: this.calendarService.getScreen(),
    })
      .pipe(
        finalize(() => {
          this.loadingConfig = false;
          this.ref.markForCheck();
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: ({ descReferenceFields, dateReferenceFields, screenFields, screen }) => {
          this.dateFields = dateReferenceFields.sort((a, b) => a.label.localeCompare(b.label));
          this.calendarDateField = this.getCalendarDateField(dateReferenceFields, screen?.dateFieldId);
          this.calendarDescField = this.getCalendarDescField(descReferenceFields, screen?.descriptionFieldId);
          this.screenFields = screenFields?.data || [];
        },
        error: error => this.notificationService.error(error),
      });
  }

  private getCalendarDateField(
    dateReferenceFields: CalendarReferenceField[],
    dateFieldId?: number,
  ): CalendarReferenceField {
    if (!dateReferenceFields.length) {
      return undefined;
    }

    const found = dateReferenceFields.find(f => f.id == dateFieldId);
    return found ? found : dateReferenceFields[0];
  }

  private getCalendarDescField(
    dateReferenceFields: CalendarReferenceField[],
    descFieldId?: number,
  ): CalendarReferenceField | undefined {
    if (!dateReferenceFields.length || !descFieldId) {
      return undefined;
    }

    return dateReferenceFields.find(f => f.id == descFieldId);
  }

  private setCalendarDate(dt: moment.Moment): void {
    this.calendarDate = {
      date: dt.toDate(),
      year: dt.year(),
      month: dt.month(),
      week: dt.isoWeek(),
      day: dt.date(),
    };
  }

  private closeAllCalendarDialogs(event: any) {
    const dialogMoreButtonClicked = event?.target?.closest('.more');
    const dialogDocMiniClicked = event?.target?.closest('.doc-mini');
    const dialogClicked = event?.target?.closest('.mat-mdc-dialog-container');

    // check if the target of the click is one of the calendar clickable elements that should not close the calendar dialogs
    if (dialogClicked || dialogMoreButtonClicked || dialogDocMiniClicked) {
      return;
    }

    // find and close all calendar dialogs
    const calendarDialogs = this.dialog.openDialogs.filter(
      d => d.id && (d.id.startsWith(CalendarDialogMorePrefix) || d.id.startsWith(CalendarDialogDocDetailPrefix)),
    );
    for (const dialog of calendarDialogs) {
      dialog.close();
    }
  }
}
